import { useEffect, useState } from "react";
import { apiGet, MSG_LOAD_FAILED } from "@/lib/api";
import { useToast } from "@/components/feature/ToastProvider";

interface UseApiResult<T> {
  data: T | null;
  total: number;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * 외부 API 데이터 로딩 훅.
 * - rst:1 이면 data 반환 (빈 배열/빈 문자열은 페이지에서 빈 상태로 처리)
 * - rst:0 이면 msg 를 토스트로 노출
 * - 404/네트워크 오류면 공통 실패 문구 + [다시 시도] 용 error 반환
 */
export function useApi<T>(path: string | null): UseApiResult<T> {
  const { toast } = useToast();
  const [data, setData] = useState<T | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(!!path);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    apiGet<T>(path)
      .then((res) => {
        if (!active) return;
        setData(res.data);
        setTotal(res.total ?? 0);
      })
      .catch((e: Error & { code?: number; status?: number }) => {
        if (!active) return;
        const msg = e.message || MSG_LOAD_FAILED;
        setError(msg);
        setData(null);
        // rst:0(비즈니스 오류)일 때만 msg 를 토스트로 노출
        if (typeof e.code === "number") {
          toast(msg, "error");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [path, tick, toast]);

  const reload = () => setTick((t) => t + 1);

  return { data, total, error, loading, reload };
}